import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    useDisclosure,
    DrawerCloseButton,
    Text,
    Link,
    Image,

    /* Hooks */
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/* Styles */
import { useMyRewardsStyles } from '../styles/myRewardsStyles'

/* Hooks */
import { useOptimizely } from '../../../contexts/optimizely-context'

/* Utils */
import { generateStyles } from '../../../components/optimizely/utils/utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

const EarningPoints = React.forwardRef(({ customer }, ref) => {
    const { rewardPageBlock } = useOptimizely()
    const styles = useMyRewardsStyles()
    const isMobile = useBreakpointValue({ base: true, lg: false })
    const erbStyling = styles?.components?.exclusiveRewardsBlock
    const epStyling = styles?.components?.earningPoints
    const [cmsData, setCmsData] = useState(null)

    const breakpointTitle = useBreakpointValue({
        base: cmsData?.title?.mobileText,
        md: cmsData?.title?.tabletText,
        lg: cmsData?.title?.desktopText
    })

    // This will fallback CMS
    const hardCodedData = {
        title: {
            mobileText: 'Ways to <span class="highlighted">Earn</span>',
            tabletText: 'EARNING <span class="highlighted">POINTS</span>, THE GARAGE COLLECTIF WAY',
            desktopText: 'EARNING <span class="highlighted">POINTS</span>, THE GARAGE COLLECTIF WAY',
        },
        subTitle: "And just like that... thanks to your points and your tier status, you're already earning rewards. Don't miss out!",
        waysToEarn: [{
            'tile-content-id': 'start-shopping-tile',
            'popup-content-id': 'start-shopping-popup',
            'completed': customer?.c_sfscOnlineOrdersCount || customer?.c_sfscInStoreOrdersCount
        }, {
            'tile-content-id': 'newsletter-tile',
            'popup-content-id': 'newsletter-popup',
            'completed': customer?.c_email_garage_optedIn
        }, {
            'tile-content-id': 'shop-tile',
            'popup-content-id': 'shop-popup',
            'completed': customer?.c_sfscOnlineOrdersCount > 0 && customer?.c_sfscInStoreOrdersCount > 0
        }, {
            'tile-content-id': 'subscribe-tile',
            'popup-content-id': 'subscribe-popup',
            'completed': customer?.c_sms_garage_optedIn
        }],
        waysToEarnDataMapping: {
            'start-shopping-tile': {
                icon: getAssetUrl('static/img/my-rewards/start-shopping.svg'),
                title: 'Start Shopping',
                subtitle: '1 point per $1',
                completedText: 'Completed'
            },
            'start-shopping-popup': {
                icon: getAssetUrl('static/img/my-rewards/start-shopping.svg'),
                title: 'Make a Purchase',
                content: `
                    <p class="er-modal-subtitle">Receive 1 point for every $1 you spend online, on the app, or in-store.</p>
                    Points will be granted:
                    <ul>
                        <li>Online & on the app, at the time of shipment</li>
                        <li>Buy online, Pick-up in-store, at the time of in-store pick-up.</li>
                        <li>In-store, at the time of purchase.</li>
                        <li>Online orders made in-store, at the time of shipment.</li>
                    </ul>
                `,
                note: 'Please allow 48 hours for the points to be added to the account. Applicable to amount spent before taxes and shipping fees. Points are rounded to the nearest dollar. Excludes purchases made on e-gift cards, gift cards, gift wrap and shopping bags. A maximum 15,000 points can be earned per anniversary year.'
            },
            'newsletter-tile': {
                icon: getAssetUrl('static/img/my-rewards/newsletter.svg'),
                title: 'Subscribe to our Newsletter',
                subtitle: '10 points',
                completedText: 'Completed'
            },
            'newsletter-popup': {
                icon: getAssetUrl('static/img/my-rewards/newsletter.svg'),
                title: 'Subscribe to our Newsletter',
                content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.',
                note: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.'
            },
            'shop-tile': {
                icon: getAssetUrl('static/img/my-rewards/shop.svg'),
                title: 'Shop Online & In-Store',
                subtitle: '5 points',
                completedText: 'Completed'
            },
            'shop-popup': {
                icon: getAssetUrl('static/img/my-rewards/shop.svg'),
                title: 'Shop Online & In-Store',
                content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.',
                note: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.'
            },
            'subscribe-tile': {
                icon: getAssetUrl('static/img/my-rewards/subscribe.svg'),
                title: 'Subscribe to SMS',
                subtitle: '10 points',
                completedText: 'Completed'
            },
            'subscribe-popup': {
                icon: getAssetUrl('static/img/my-rewards/subscribe.svg'),
                title: 'Subscribe to SMS',
                content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.',
                note: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.'
            }
        }
    }

    const cmsDataModel = (rewardPageBlockData) => {
        const waysToEarn = [];
        const waysToEarnDataMapping = {};

        rewardPageBlockData?.WaysToEarnTileList?.forEach(item => {
            const block = item.ContentLink.Expanded;
            const rewardTileSFID = block.RewardTileSFID;

            // Determine completed condition based on RewardTileSFID
            let completed;
            switch (rewardTileSFID) {
                case "start_shopping":
                    completed = customer?.c_sfscOnlineOrdersCount || customer?.c_sfscInStoreOrdersCount;
                    break;
                case "getapp":
                    // TODO FD REWARDS: Update logic with correct values that are coming from the BD side.
                    // From what I know at this point this shouldn't be configured
                    completed = false;
                    break;
                case "shop_online_store":
                    completed = customer?.c_sfscOnlineOrdersCount > 0 && customer?.c_sfscInStoreOrdersCount > 0;
                    break;
                case "get_notifications":
                    // TODO FD REWARDS: Update logic with correct values that are coming from the BD side.
                    // From what I know at this point this shouldn't be configured
                    completed = false;
                    break;
                    // TODO FD REWARDS: There is a typo here but it is intended since this is how the value is configured on CMS
                    // To be corrected after the CMS side will be corrected
                case "subsvribe_sms":
                    completed = customer?.c_sms_garage_optedIn;
                    break;
                case "":
                    // TODO FD REWARDS: This is the newsletter tile which don't have an rewardTileSFID on CMS
                    // It must be update with value after will be update on CMS
                    completed = customer?.c_email_garage_optedIn;
                    break;
                default:
                    completed = false;
            }

            // Add to waysToEarn array
            waysToEarn.push({
                'tile-content-id': `${rewardTileSFID}-tile`,
                'popup-content-id': `${rewardTileSFID}-popup`,
                'completed': completed
            });

            // Create tile mapping
            const tileKey = `${rewardTileSFID}-tile`;
            waysToEarnDataMapping[tileKey] = {
                icon: block.IconMedia?.Expanded?.Url || '',
                title: block.Title || '',
                subtitle: block.Description || '',
                completedText: 'Completed',
                titleStyle: block.RewardTileBlockTitleStyle,
                descriptionStyle: block.RewardTileBlockDescriptionStyle
            };

            // Create popup mapping
            if (block.Modal?.Expanded) {
                const modal = block.Modal.Expanded;
                const popupKey = `${rewardTileSFID}-popup`;

                waysToEarnDataMapping[popupKey] = {
                    icon: modal.IconMedia?.Expanded?.Url || '',
                    title: modal.Title || '',
                    content: modal.Description || '',
                    note: modal.AdditionalInfo || '',
                    titleStyle: modal.TileModalBlockTitleStyle,
                    descriptionStyle: modal.TileModalBlockDescriptionStyle,
                    additionalInfoStyle: modal.AdditionalInfoStyle
                };
            }
        });

        return {
            title: {
                mobileText: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.MobileText,
                tabletText: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.TabletText,
                desktopText: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.DesktopText,
                ResponsiveTextBlockTextStyle: rewardPageBlockData?.WaysToEarnTitleResponsiveText?.Expanded?.ResponsiveTextBlockTextStyle
            },
            subTitle: "And just like that... thanks to your points and your tier status, you're already earning rewards. Don't miss out!",
            waysToEarn,
            waysToEarnDataMapping
        };
    }

    useEffect(() => {
        if (rewardPageBlock) {
            setCmsData(cmsDataModel(rewardPageBlock))
        } else {
            setCmsData(hardCodedData)
        }
    }, [rewardPageBlock])

    const rewards = cmsData?.waysToEarn
    const rewardsDataMapping = cmsData?.waysToEarnDataMapping

    // Refs and state for popup handling
    const containerRef = useRef(null)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [popupContent, setPopupContent] = useState(null)

    const generateTileComponent = (tileData, isCompleted) => {
        return (
            <Box {...erbStyling.erTile}>
                <Image {...erbStyling.erIcon} src={tileData.icon} />
                <Link
                    {...generateStyles(tileData?.titleStyle, {...erbStyling.erTitle})}
                    href="#"
                    data-popup-trigger
                >
                    {tileData.title}
                </Link>
                <Text {...generateStyles(tileData?.descriptionStyle, {...erbStyling.erSubtitle})}>
                    {tileData.subtitle}
                </Text>
                {isCompleted && customer.isRegistered &&
                    <Text {...erbStyling.erCompleted}>
                        {tileData.completedText}
                    </Text>
                }
            </Box>
        )
    }

    const generatePopupComponent = (popupData) => {
        return (
            <Box className="er-modal-tile">
                <Image {...styles.erModalIcon} src={popupData.icon} />
                <Text {...generateStyles(popupData?.titleStyle, {...styles.erModalTitle})}>
                    {popupData.title}
                </Text>
                <Box
                    className="er-modal-subtitle"
                    {...generateStyles(popupData?.descriptionStyle, {...styles.erModalSubtitle})}
                    dangerouslySetInnerHTML={{ __html: popupData.content }}
                />
                <Box
                    className="er-modal-note"
                    {...generateStyles(popupData?.additionalInfoStyle, {...styles.erModalNote})}
                    dangerouslySetInnerHTML={{ __html: popupData.note }}
                />
            </Box>
        )
    }

    const handlePopupClick = (event) => {
        event.preventDefault()
        const trigger = event.target.closest('[data-popup-trigger]')

        if (!trigger) return

        const tileWrapper = trigger.closest('[data-tile-index]')

        if (!tileWrapper) return

        const tileIndex = parseInt(tileWrapper.getAttribute('data-tile-index'))
        const reward = rewards?.[tileIndex]

        if (reward) {
            const drawerContent = rewardsDataMapping[reward['popup-content-id']]
            if (drawerContent) {
                setPopupContent(generatePopupComponent(drawerContent))
                onOpen()
            }
        }
    }

    // Event delegation for popup triggers
    useEffect(() => {
        const container = containerRef.current
        if (container) {
            container.addEventListener('click', handlePopupClick)
            return () => {
                container.removeEventListener('click', handlePopupClick)
            }
        }
    }, [rewards, rewardsDataMapping, onOpen])

    if (rewards?.length === 0) {
        return
    }

    return (
        <Box ref={ref}>
            <Box {...epStyling.container}
                ref={containerRef}>
                <Box {...styles.componentWrapper}>
                    <Box
                        {...generateStyles(cmsData?.title?.ResponsiveTextBlockTextStyle, { ...styles.sectionTitle })}
                        {...epStyling.title}
                        sx={styles.highlighted}
                        dangerouslySetInnerHTML={{ __html: breakpointTitle }} />
                    <Box {...erbStyling.tiles} {...erbStyling.mobileGrid}>
                        {rewards?.map((reward, index) => {
                            const tileData = rewardsDataMapping[reward['tile-content-id']]
                            return (tileData &&
                                <Box
                                    key={index}
                                    sx={erbStyling.gridTileWrapper}
                                    data-tile-index={index}
                                >
                                    {generateTileComponent(tileData, reward.completed)}
                                </Box>
                            )
                        })}
                    </Box>
                </Box>
            </Box>

            <Drawer
                placement="right"
                isOpen={isOpen}
                onClose={onClose}
                onOpen={onOpen}
                size="md"
                scrollBehavior="inside"
                isFullHeight={true}
            >
                <DrawerOverlay />
                <DrawerContent {...styles.drawerContent}>
                    <DrawerCloseButton />
                    <DrawerBody {...styles.drawerBody}>
                        <Box sx={styles.drawerWrapper}>
                            {popupContent}
                        </Box>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
})

EarningPoints.propTypes = {
    customer: PropTypes.object
}

export default EarningPoints
